import {
  afterEveryRender,
  DestroyRef,
  DOCUMENT,
  ElementRef,
  inject,
} from '@angular/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { DIALOG_SELECTOR, topmostDialog } from './dialog-dom';

/** Class of the × PrimeNG renders in the dialog header. */
const CLOSE_BUTTON_SELECTOR = '.p-dialog-close-button';

export interface BdDialogCloseBehavior {
  /**
   * While this returns true, Escape and × do not close the dialog (an operation is
   * running). The × is hidden for that time.
   */
  busy?: () => boolean;
  /** Runs instead of closing when Escape is pressed while busy, e.g. to stop the operation. */
  onEscapeWhileBusy?: () => void;
  /**
   * Result for a close without a result (Escape, ×, `ref.close()`), e.g. so that Escape after
   * a finished operation reports the same result as the dialog's "Schließen" button.
   */
  resultOnClose?: () => unknown;
}

/**
 * Adapts Escape and × for a DynamicDialog content component whose dialog must not simply
 * close with `undefined` (BDA-6419). Call it in the component's injection context.
 */
export function bdDialogCloseBehavior(behavior: BdDialogCloseBehavior): void {
  const ref = inject(DynamicDialogRef);
  // Optional: logic specs construct the component without a host element.
  const host = inject<ElementRef<HTMLElement>>(ElementRef, {
    optional: true,
  })?.nativeElement;
  const document = inject(DOCUMENT);
  const { busy, onEscapeWhileBusy, resultOnClose } = behavior;

  if (resultOnClose) {
    const close = ref.close.bind(ref);
    ref.close = (result?: unknown): void =>
      close(result === undefined ? resultOnClose() : result);
  }

  if (!busy || !host) {
    return;
  }

  // Capture phase on document: runs before PrimeNG's own Escape listener (bubble phase on
  // document) and keeps the event from reaching it.
  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !busy()) {
      return;
    }
    const dialog = host.closest(DIALOG_SELECTOR);
    if (!dialog || dialog !== topmostDialog(document)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    onEscapeWhileBusy?.();
  };
  document.addEventListener('keydown', onKeydown, true);
  inject(DestroyRef).onDestroy(() =>
    document.removeEventListener('keydown', onKeydown, true),
  );

  // Render hook instead of an effect: `busy` may read a plain field, not only a signal.
  afterEveryRender({
    write: () => {
      const closeButton = host
        .closest(DIALOG_SELECTOR)
        ?.querySelector<HTMLElement>(CLOSE_BUTTON_SELECTOR);
      if (closeButton) {
        closeButton.style.visibility = busy() ? 'hidden' : '';
      }
    },
  });
}
